// Bulk import of collaborators from a CSV file. Pure helpers (tested in collaboratorImport.test.js).
// Every row is then created through the normal POST /collaborators, one at a time: the server applies exactly the same
// rules as the form (duplicates, active referentials, dates, idempotency, audit). Nothing here is a security boundary.

export const MAX_ROWS = 500;

const TEMPLATE_HEADERS = ["prenom", "nom", "fonction", "type_relation", "statut", "date_entree", "service", "email", "telephone", "whatsapp"];
const TEMPLATE_EXAMPLE = ["Awa", "Ndiaye", "Formatrice POS", "Salarié", "actif", "2026-09-01", "Formation", "awa.ndiaye@jamora.sn", "+221 77 123 45 67", ""];

const fold = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Header synonyms, compared folded (no accents, no case, no punctuation).
const HEADER_ALIASES = {
  first_name: ["prenom", "first name", "firstname"],
  last_name: ["nom", "nom de famille", "last name", "lastname"],
  job_title: ["fonction", "poste", "job title", "role metier"],
  relation_type: ["type relation", "type de relation", "type", "relation"],
  status: ["statut", "status"],
  entry_date: ["date entree", "date d entree", "entree", "entry date"],
  service: ["service"],
  email: ["email", "e mail", "courriel", "mail"],
  phone: ["telephone", "tel", "phone", "portable"],
  whatsapp: ["whatsapp", "whats app"],
};

export function decodeFile(buffer) {
  const utf8 = new TextDecoder("utf-8").decode(buffer);
  // Excel on Windows often saves CSV in Windows-1252: a replacement character means UTF-8 was the wrong guess.
  return (utf8.includes("�") ? new TextDecoder("windows-1252").decode(buffer) : utf8).replace(/^﻿/, "");
}

// RFC 4180-style parser: quoted fields, doubled quotes, CRLF; ";" (French Excel) or "," detected on the header line.
export function parseCsv(text) {
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const delimiter = (firstLine.match(/;/g) || []).length >= (firstLine.match(/,/g) || []).length ? ";" : ",";
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(field); rows.push(row); row = []; field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

export function mapHeaders(headerRow) {
  const mapping = {};
  const unknown = [];
  headerRow.forEach((raw, index) => {
    const key = Object.keys(HEADER_ALIASES).find((k) => HEADER_ALIASES[k].includes(fold(raw)));
    if (key && mapping[key] === undefined) mapping[key] = index;
    else if (raw.trim()) unknown.push(raw.trim());
  });
  return { mapping, unknown };
}

// 9-digit Senegalese numbers (Excel drops the "+221" or the leading 0): add the country code, never guess anything else.
export function normalizePhone(raw) {
  const value = (raw || "").trim();
  if (!value) return "";
  const compact = value.replace(/[\s.\-()]/g, "");
  if (/^[37]\d{8}$/.test(compact)) return `+221${compact}`;
  return compact;
}

export function normalizeDate(raw) {
  const value = (raw || "").trim();
  if (!value) return "";
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return value;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return null;
}

const STATUS_WORDS = { actif: "active", active: "active", preinscrit: "pre_registered", "pre inscrit": "pre_registered", preenregistre: "pre_registered", "pre enregistre": "pre_registered" };

// One CSV row → the POST /collaborators body, or the reasons it cannot be sent. `refs` = active referentials.
export function buildRow(cells, mapping, refs, today) {
  const get = (key) => (mapping[key] === undefined ? "" : (cells[mapping[key]] || "").trim());
  const errors = [];
  const firstName = get("first_name");
  const lastName = get("last_name");
  if (!firstName && !lastName) errors.push("Prénom ou nom manquant");

  const typeText = get("relation_type");
  const activeTypes = refs.relation_types.filter((r) => r.active);
  let relationType = null;
  if (typeText) {
    const match = activeTypes.find((r) => fold(r.label) === fold(typeText) || fold(r.code) === fold(typeText));
    if (match) relationType = match.code;
    else errors.push(`Type de relation inconnu : « ${typeText} »`);
  } else {
    relationType = (activeTypes.find((r) => r.code === "salarie") || activeTypes[0])?.code || null;
    if (!relationType) errors.push("Aucun type de relation actif");
  }

  const statusText = fold(get("status"));
  let status = "pre_registered";
  if (statusText) {
    if (STATUS_WORDS[statusText] || STATUS_WORDS[statusText.replace(/ /g, "")]) status = STATUS_WORDS[statusText] || STATUS_WORDS[statusText.replace(/ /g, "")];
    else errors.push(`Statut inconnu : « ${get("status")} » (actif ou préinscrit)`);
  }
  const entryDate = normalizeDate(get("entry_date"));
  if (entryDate === null) errors.push(`Date d'entrée illisible : « ${get("entry_date")} » (AAAA-MM-JJ ou JJ/MM/AAAA)`);
  if (status === "active" && !entryDate && entryDate !== null) errors.push("Date d'entrée obligatoire pour une personne active");
  if (status === "active" && entryDate && entryDate > today) errors.push("La date d'entrée ne peut pas être dans le futur");

  const serviceText = get("service");
  let serviceId = null;
  if (serviceText) {
    const match = refs.services.filter((s) => s.active).find((s) => fold(s.name) === fold(serviceText));
    if (match) serviceId = match.id;
    else errors.push(`Service inconnu ou inactif : « ${serviceText} »`);
  }

  const contacts = {};
  const email = get("email");
  if (email) {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) contacts.email = email;
    else errors.push(`Email invalide : « ${email} »`);
  }
  const phone = normalizePhone(get("phone"));
  if (phone) contacts.phone = phone;
  const whatsapp = normalizePhone(get("whatsapp"));
  if (whatsapp) contacts.whatsapp = whatsapp;

  const jobTitle = get("job_title");
  if (jobTitle.length > 120) errors.push("Fonction trop longue (120 caractères maximum)");

  const payload = {
    first_name: firstName || null, last_name: lastName || null, job_title: jobTitle || null,
    relation_type: relationType, status, service_id: serviceId, contacts,
    ...(status === "active" ? { entry_date: entryDate } : {}),
  };
  return { payload, errors, display: [firstName, lastName].filter(Boolean).join(" ") };
}

export function templateCsv() {
  const line = (cells) => cells.map((c) => (/[;"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(";");
  return `﻿${line(TEMPLATE_HEADERS)}\r\n${line(TEMPLATE_EXAMPLE)}\r\n`;
}
